/**
 * The beat after a payment clears.
 *
 * One card, under half a second, and no confetti: it rises, the mark is drawn,
 * the row settles into place behind it, and it ends on the two things anyone
 * actually wants next. The rank is left as a dash until the payment has been
 * confirmed and the board has been re-read — inventing a position here would
 * contradict the line directly above it.
 */

import { Link } from 'react-router-dom';
import type { ReactNode } from 'react';

import {
  getSubcategory,
  type Company,
  type Job,
  type LeaderboardScope,
} from '@bidtohire/shared';

import {
  compactCompensationLabel,
  compensationLabel,
  locationLabel,
  seniorityLabel,
} from '../lib/format';
import { useLeaderboard } from '../lib/queries';
import { BoardRowPreview } from './ListingPreview';
import { Reels } from './Odometer';

/** The key the bid panel leaves behind before handing over to Stripe. */
export const PUBLISHING_KEY = 'bidtohire.publishing';

export function rememberPublishing(jobId: string): void {
  try {
    window.sessionStorage.setItem(PUBLISHING_KEY, jobId);
  } catch {
    // A browser refusing storage simply gets the plain confirmation.
  }
}

export function readPublishing(): string | undefined {
  try {
    return window.sessionStorage.getItem(PUBLISHING_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}

export function forgetPublishing(): void {
  try {
    window.sessionStorage.removeItem(PUBLISHING_KEY);
  } catch {
    // Nothing to clear.
  }
}

export function PublishedBeat({
  job,
  company,
  headline,
  note,
  onPostAnother,
}: {
  readonly job: Job;
  readonly company: Pick<Company, 'name' | 'slug'> & { readonly logoKey?: string };
  /**
   * What just happened. A raise arrives here from Stripe with the payment
   * still settling; a listing that has just been published arrives with the
   * money long since cleared, and telling it to wait for Stripe would be
   * describing something that already happened.
   */
  readonly headline?: string;
  readonly note?: string;
  readonly onPostAnother: () => void;
}): ReactNode {
  const subcategory = getSubcategory(job.category, job.subcategory);
  const scope: LeaderboardScope = {
    kind: 'subcategory',
    category: job.category,
    subcategory: job.subcategory,
  };
  const board = useLeaderboard(scope);
  // The board's own row for this listing carries the server's rank. Counting
  // the loaded page instead would cap the figure at the page size, so a listing
  // on a busy specialism would be told it placed higher than it did — on the
  // screen that confirms what the money bought.
  const rank = (board.data?.pages ?? [])
    .flatMap((page) => page.items)
    .find((entry) => entry.jobId === job.id)?.rank;

  const subline = [company.name, subcategory?.name, seniorityLabel(job.seniority)]
    .filter((part) => part !== undefined && part !== '')
    .join(' · ');

  return (
    <div className="card beat" role="status">
      <div className="beat-head">
        <span className="disc is-done is-drawn" aria-hidden="true">
          <svg width="13" height="13" viewBox="0 0 12 12" fill="none">
            <path
              className="tick"
              d="M2.6 6.2l2.3 2.3L9.4 3.9"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
        {headline ?? 'Payment received.'}
      </div>
      <p className="small muted" style={{ marginTop: 6 }}>
        {note ?? 'The position updates when Stripe confirms it.'}
      </p>

      <div className="pv-frame">
        <BoardRowPreview
          rank={
            rank === undefined ? (
              '—'
            ) : (
              <Reels text={String(rank)} order={rank} variant="field" label={`Rank ${rank}`} />
            )
          }
          company={company}
          title={job.title}
          subline={subline}
          salary={compactCompensationLabel(compensationLabel(job.compensation))}
          location={locationLabel(job.locations, job.workplaceType)}
          priceCents={job.totalPaidCents}
        />
      </div>

      <div className="beat-actions">
        <Link className="pill pill-primary" to={`/jobs/${job.id}`}>
          See it on the board
        </Link>
        <button type="button" className="pill pill-quiet" onClick={onPostAnother}>
          Post another role
        </button>
      </div>
    </div>
  );
}
